import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { HexColorPicker } from 'react-colorful'
import classNames from 'classnames'
import { toastStore } from 'shared/ui/Toast/store/toast'
import { Box } from 'shared/ui/Box'
import { Dropdown, DropdownContent } from 'shared/ui/Dropdown'
import { IconButton } from 'shared/ui/IconButton'
import { Typography } from 'shared/ui/Typography'
import { getAriaLabel } from 'shared/lib'
import { TextField } from 'shared/ui/TextField'
import { getCorrectHEXColor } from 'shared/lib/getCorrectHEXColor'

import styles from './styles.module.scss'
import './styles.css'

type IColorPickerProps = {
  color: string
  setColor: (color: string) => void
  isAutocorrectEnabled?: boolean
}

export const ColorPicker = observer(
  ({ color, setColor, isAutocorrectEnabled = true }: IColorPickerProps) => {
    const [accentColor, setAccentColor] = useState(color)
    const [isFocused, setIsFocused] = useState(false)

    const typeAccentColor = (value: string) => {
      const cleanedValue = value.replace(/[^0-9A-Fa-f]/g, '')
      if (cleanedValue.length > 6) return

      setAccentColor(`#${cleanedValue}`)
    }

    const handleSetColor = () => {
      const correctHEXColor = isAutocorrectEnabled
        ? getCorrectHEXColor(accentColor)
        : accentColor.length === 7
        ? accentColor
        : color

      if (!isAutocorrectEnabled) checkValidate()
      setColor(correctHEXColor)
    }

    const checkValidate = () => {
      if (accentColor.length !== 7) {
        toastStore.add({
          title: 'Enter a valid HEX color code (e.g. #FF0000)',
          type: 'error',
        })
      }
    }

    const handleFocus = () => setIsFocused(true)

    const handleBlur = () => setIsFocused(false)

    return (
      <Dropdown
        onChangeOpen={(open) => {
          if (open) setAccentColor(color)
        }}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: { offset: [-8, -52] },
            },
          ],
        }}
        width={252}
        placement={'bottom-start'}
        className={styles.colorPickerDropdown}
        triggerComponent={() => (
          <div className={classNames(styles.colorPickerInput, styles.trigger)}>
            <Box display={'flex'} alignItems={'center'} gap={'12px'} justifyContent={'flex-start'}>
              <div className={styles.color} style={{ backgroundColor: color }} />
              <Typography
                ariaLabel={getAriaLabel('ColorPicker_color')}
                color='var(--content-primary-primary)'
                variant='text-input-field-r'
              >
                {color}
              </Typography>
            </Box>
          </div>
        )}
        customComponent={(onClose) => (
          <DropdownContent
            noPadding
            className={classNames('colorPickerContainer', styles.colorPickerContainer)}
          >
            <HexColorPicker color={accentColor} onChange={setAccentColor} />
            <div
              className={classNames(styles.colorPickerInput, {
                [styles.colorPickerInput_focused]: isFocused,
              })}
            >
              <Box
                display={'flex'}
                alignItems={'center'}
                gap={'12px'}
                justifyContent={'flex-start'}
              >
                <div className={styles.color} style={{ backgroundColor: accentColor }} />
                <TextField
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  InputProps={{
                    placeholder: '#000000',
                  }}
                  size='small'
                  variant='integrated'
                  value={accentColor}
                  onChange={typeAccentColor}
                  ariaLabel='ColorPickerInput'
                  className={styles.colorPickerColorInput}
                />
              </Box>
              <IconButton
                ariaLabel={getAriaLabel('ColorPicker_submit')}
                icon='check1'
                size='small'
                onClick={() => {
                  handleSetColor()
                  onClose()
                }}
              />
            </div>
          </DropdownContent>
        )}
      />
    )
  }
)
